import {
  listChatSessions,
  type ChatSession,
} from '@/app/services/chat';
import {
  getDeepResearchHistory,
  type DeepResearchHistoryItem,
} from '@/app/services/deep-research';
import {
  getTranslateHistory,
  type TranslationSession,
} from '@/app/services/translation';
import type { QueryClient } from '@tanstack/react-query';

export const SIDEBAR_SEARCH_HISTORY_LIMIT = 25;
export const SIDEBAR_TRANSLATE_HISTORY_LIMIT = 25;
export const SIDEBAR_DEEP_RESEARCH_HISTORY_LIMIT = 25;

export const sidebarHistoryKeys = {
  search: ['sidebar-history', 'search'] as const,
  translate: ['sidebar-history', 'translate'] as const,
  deepResearch: ['sidebar-history', 'deep-research'] as const,
};

export async function fetchSidebarSearchHistory() {
  const { data } = await listChatSessions({
    offset: 0,
    size: SIDEBAR_SEARCH_HISTORY_LIMIT,
  });
  return data.items;
}

export async function fetchSidebarTranslationHistory() {
  const { data } = await getTranslateHistory({
    offset: 0,
    limit: SIDEBAR_TRANSLATE_HISTORY_LIMIT,
  });
  return data.list;
}

export async function fetchSidebarDeepResearchHistory() {
  const { data } = await getDeepResearchHistory({
    offset: 0,
    limit: SIDEBAR_DEEP_RESEARCH_HISTORY_LIMIT,
  });
  return data.list;
}

function upsertFirst<T>(
  items: T[] | undefined,
  incoming: T,
  match: (item: T) => boolean,
  limit: number,
) {
  const nextItems = items?.filter((item) => !match(item)) ?? [];
  return [incoming, ...nextItems].slice(0, limit);
}

function patchItems<T>(
  items: T[] | undefined,
  match: (item: T) => boolean,
  patch: (item: T) => T,
) {
  if (!items) {
    return items;
  }
  return items.map((item) => (match(item) ? patch(item) : item));
}

export function patchSidebarSearchHistory(
  queryClient: QueryClient,
  sessionId: string,
  patch: Partial<ChatSession>,
) {
  queryClient.setQueryData(
    sidebarHistoryKeys.search,
    (items?: ChatSession[]) =>
      patchItems(items, (item) => item.session_id === sessionId, (item) => ({
        ...item,
        ...patch,
      })),
  );
}

export function insertSidebarSearchHistory(
  queryClient: QueryClient,
  item: ChatSession,
) {
  queryClient.setQueryData(sidebarHistoryKeys.search, (items?: ChatSession[]) =>
    upsertFirst(
      items,
      item,
      (current) => current.session_id === item.session_id,
      SIDEBAR_SEARCH_HISTORY_LIMIT,
    ),
  );
}

export function removeSidebarSearchHistory(
  queryClient: QueryClient,
  sessionId: string,
) {
  queryClient.setQueryData(
    sidebarHistoryKeys.search,
    (items?: ChatSession[]) =>
      items?.filter((item) => item.session_id !== sessionId) ?? [],
  );
}

export function patchSidebarDeepResearchHistory(
  queryClient: QueryClient,
  drid: string,
  patch: Partial<DeepResearchHistoryItem>,
) {
  queryClient.setQueryData(
    sidebarHistoryKeys.deepResearch,
    (items?: DeepResearchHistoryItem[]) =>
      patchItems(items, (item) => item.drid === drid, (item) => ({
        ...item,
        ...patch,
      })),
  );
}

export function insertSidebarDeepResearchHistory(
  queryClient: QueryClient,
  item: DeepResearchHistoryItem,
) {
  queryClient.setQueryData(
    sidebarHistoryKeys.deepResearch,
    (items?: DeepResearchHistoryItem[]) =>
      upsertFirst(
        items,
        item,
        (current) => current.drid === item.drid,
        SIDEBAR_DEEP_RESEARCH_HISTORY_LIMIT,
      ),
  );
}

export function patchSidebarTranslationHistory(
  queryClient: QueryClient,
  sessionId: string,
  patch: Partial<TranslationSession>,
) {
  queryClient.setQueryData(
    sidebarHistoryKeys.translate,
    (items?: TranslationSession[]) =>
      patchItems(
        items,
        (item) => item.file_translation_session_id === sessionId,
        (item) => ({ ...item, ...patch }),
      ),
  );
}

export function insertSidebarTranslationHistory(
  queryClient: QueryClient,
  item: TranslationSession,
) {
  queryClient.setQueryData(
    sidebarHistoryKeys.translate,
    (items?: TranslationSession[]) =>
      upsertFirst(
        items,
        item,
        (current) =>
          current.file_translation_session_id === item.file_translation_session_id,
        SIDEBAR_TRANSLATE_HISTORY_LIMIT,
      ),
  );
}

export function isSidebarTranslationProcessing(status: TranslationSession['status']) {
  return (
    status === 'PROGRESS' ||
    status === 'INIT' ||
    status === 'ASYNC' ||
    status === 'WAIT_IN_QUEUE'
  );
}
